import { useEffect, useMemo, useState } from 'react';
import { GeoJSON, useMap } from 'react-leaflet';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { api } from '../lib/api';
import { GRADOS_REZAGO, type GradoRezago, type SocioAgebItem } from '../lib/types';
import { readBbox } from '../lib/mapBbox';

/**
 * Capa socioeconómica por AGEB urbana (RFC-0022).
 *
 * Censo INEGI 2020 + Grado de Rezago Social de CONEVAL. Misma mecánica
 * que HeatmapLayer: bbox debounced, un AbortController por request y
 * remount del <GeoJSON> por contador de fetch (react-leaflet no reacciona
 * a cambios de `data`, y usar items.length como key congela los polígonos
 * al pasar de una zona con N AGEBs a otra con N).
 *
 * OJO CON LA SEMÁNTICA: rezago "Muy bajo" = nivel socioeconómico ALTO.
 * Va invertido respecto al NSE de AMAI, así que la escala de color va de
 * verde (muy bajo rezago = mejor) a rojo (muy alto rezago = peor), y la
 * leyenda lo dice explícitamente.
 */

const MIN_ZOOM_FOR_GEOM = 10; // un poco antes que el heatmap: AGEBs socio
const BBOX_DEBOUNCE_MS = 600;  // son más informativas en vista de ciudad

interface SocioProperties {
  cvegeo: string;
  grado: GradoRezago | null;
  pobtot: number | null;
  graproes: number | null;
  pctInternet: number | null;
  pctAuto: number | null;
  pctSinTic: number | null;
  proOcupC: number | null;
}

/** NUMERIC de Postgres llega como string; null-safe. */
function num(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function SocioLayer() {
  const [items, setItems] = useState<SocioAgebItem[]>([]);
  const [fetchSeq, setFetchSeq] = useState(0);
  const [zoom, setZoom] = useState(0);
  const map = useMap();

  useEffect(() => {
    setZoom(map.getZoom());
    let timer: number | undefined;
    let inflight: AbortController | null = null;

    const refetch = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const z = map.getZoom();
        setZoom(z);
        if (z < MIN_ZOOM_FOR_GEOM) {
          inflight?.abort();
          inflight = null;
          setItems([]);
          return;
        }
        // Sin alto de contenedor el bbox sale degenerado (sur === norte) y
        // la petición vuelve vacía — mismo guard que el resto de capas.
        const bbox = readBbox(map);
        if (!bbox) { map.invalidateSize(); return; }
        inflight?.abort();
        const ctrl = new AbortController();
        inflight = ctrl;
        api
          .socioAgebs({ withGeometry: true, bbox, limit: 500 }, ctrl.signal)
          .then((r) => {
            if (inflight !== ctrl) return; // respuesta fuera de orden
            setItems(r.items);
            setFetchSeq((s) => s + 1);
          })
          .catch(() => {});
      }, BBOX_DEBOUNCE_MS);
    };

    refetch();
    map.on('moveend', refetch);
    map.on('zoomend', refetch);
    return () => {
      map.off('moveend', refetch);
      map.off('zoomend', refetch);
      inflight?.abort();
      window.clearTimeout(timer);
    };
  }, [map]);

  const fc = useMemo<FeatureCollection<Geometry, SocioProperties>>(() => ({
    type: 'FeatureCollection',
    features: items
      .filter((i) => !!i.geometry)
      .map((i): Feature<Geometry, SocioProperties> => ({
        type: 'Feature',
        geometry: i.geometry as Geometry,
        properties: {
          cvegeo: i.cvegeo,
          grado: i.grado_rezago_social,
          pobtot: i.pobtot,
          graproes: num(i.graproes),
          pctInternet: num(i.pct_internet),
          pctAuto: num(i.pct_automovil),
          pctSinTic: num(i.pct_sin_tic),
          proOcupC: num(i.pro_ocup_c),
        },
      })),
  }), [items]);

  if (zoom < MIN_ZOOM_FOR_GEOM) return null;

  return (
    <GeoJSON
      key={fetchSeq}
      data={fc}
      style={(f) => {
        const grado = (f?.properties as SocioProperties | undefined)?.grado ?? null;
        return {
          fillColor: gradoToColor(grado),
          fillOpacity: grado ? 0.5 : 0.15,
          color: '#0a0f1f',
          weight: 0.5,
        };
      }}
      onEachFeature={(feature, layer) => {
        const p = feature.properties as SocioProperties;
        const esc = (s: string) =>
          s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const fila = (label: string, v: number | null, suf = '') =>
          v === null ? '' : `<div>${label}: <b>${v}${suf}</b></div>`;
        layer.bindPopup(
          `<div class="popup-card">
            <div style="font-weight:600">AGEB ${esc(p.cvegeo.trim())}</div>
            <div style="margin-top:4px">Rezago social: <b>${esc(p.grado ?? 'sin dato')}</b></div>
            ${fila('Población', p.pobtot)}
            ${fila('Escolaridad promedio', p.graproes, ' años')}
            ${fila('Con internet', p.pctInternet, '%')}
            ${fila('Con automóvil', p.pctAuto, '%')}
            ${fila('Sin ninguna TIC', p.pctSinTic, '%')}
            ${fila('Ocupantes por cuarto', p.proOcupC)}
            <div style="margin-top:6px;font-size:10px;opacity:.7">
              INEGI Censo 2020 · CONEVAL 2020
            </div>
          </div>`,
        );
      }}
    />
  );
}

/**
 * Escala del grado de rezago. Verde = rezago MUY BAJO = mejor condición
 * socioeconómica; rojo = rezago muy alto. Es el orden inverso al que
 * sugiere la palabra "nivel", y por eso la leyenda lleva las dos etiquetas.
 */
const GRADO_COLORS: Record<GradoRezago, string> = {
  'Muy bajo': 'rgb(34,197,94)',   // green-500  → mejor
  'Bajo': 'rgb(132,204,22)',      // lime-500
  'Medio': 'rgb(251,191,36)',     // amber-400
  'Alto': 'rgb(249,115,22)',      // orange-500
  'Muy alto': 'rgb(239,68,68)',   // red-500    → peor
};

export function gradoToColor(grado: GradoRezago | null): string {
  if (!grado) return 'rgb(120,120,120)';
  return GRADO_COLORS[grado] ?? 'rgb(120,120,120)';
}

export { GRADOS_REZAGO };
